import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Gift, Share2, Copy, Lock, Check, ShieldCheck, Sprout, Star, Package,
  BookOpen, Award, MapPin, ArrowDownLeft, ArrowUpRight, Clock, CheckCircle2, Apple,
  Leaf, Flower2, Carrot, Circle, X, Cake,
} from 'lucide-react';
import Barcode from 'react-barcode';

const GROWTH_STAGES = [
  { Icon: Circle, label: 'Just planted', min: 0 },
  { Icon: Sprout, label: 'Sprouting', min: 0.2 },
  { Icon: Leaf, label: 'Leafing out', min: 0.4 },
  { Icon: Flower2, label: 'Blooming', min: 0.65 },
  { Icon: Carrot, label: 'Almost ripe!', min: 0.85 },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  forest: '#1A4331', forestDeep: '#123524', gold: '#C9A85B', leaf: '#6B9F4A',
  leafDeep: '#4E7A34', terracotta: '#E2725B', cream: '#FAF6EC', sage: '#F0F2EB',
  text: '#2E3A2E', muted: '#5C665C', border: '#E3E7DA',
};

const REWARD_ICON = { shop_credit: Gift, digital: BookOpen, membership: Star, physical: Package, partner: Award };
const TABS = [
  { key: 'rewards', label: 'Rewards' }, { key: 'partners', label: 'Partners' },
  { key: 'events', label: 'Events' }, { key: 'activity', label: 'Activity' }, { key: 'tiers', label: 'Tiers' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Collective() {
  const [card, setCard] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [partners, setPartners] = useState([]);
  const [events, setEvents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rewards');
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [qrBig, setQrBig] = useState(false);
  const [bdOpen, setBdOpen] = useState(false);
  const [bdMon, setBdMon] = useState(null);
  const [bdDay, setBdDay] = useState('');
  const [bdGift, setBdGift] = useState(null);

  const load = useCallback(async () => {
    const g = (p, f) => axios.get(`${API}${p}`, { withCredentials: true }).then((r) => r.data).catch(() => f);
    const [c, t, r, p, a, ev, rf] = await Promise.all([
      g('/collective/card', null), g('/collective/tiers', null), g('/collective/rewards', { rewards: [] }),
      g('/collective/partners', { partners: [] }), g('/collective/activity', { activity: [] }),
      g('/collective/events', { events: [] }), g('/collective/referral', null),
    ]);
    setCard(c); setTiers(t); setRewards(r); setPartners(p?.partners || []);
    setActivity(a?.activity || []); setEvents(ev?.events || []); setReferral(rf);
    if (c?.birthday_gift) setBdGift({ points: c.birthday_gift, message: c.birthday_message });
    setLoading(false);
  }, []);

  const saveBirthday = async () => {
    if (bdMon === null || !bdDay) return;
    const date = `${String(bdMon).padStart(2, '0')}-${String(+bdDay).padStart(2, '0')}`;
    try {
      const res = await axios.post(`${API}/collective/birthday`, { date }, { withCredentials: true });
      setBdOpen(false);
      if (res.data?.birthday_gift) setBdGift({ points: res.data.birthday_gift, message: res.data.birthday_message });
      else setToast('Birthday saved 🎂');
      await load();
    } catch (e) { setToast(e?.response?.data?.detail || "Couldn't save birthday"); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!toast) return; const x = setTimeout(() => setToast(null), 2800); return () => clearTimeout(x); }, [toast]);

  // Referral attribution from ?ref=CODE
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (!code) return;
    axios.post(`${API}/collective/referral/attribute`, { code }, { withCredentials: true })
      .then((r) => { if (r.data?.attributed) { setToast(`Referral applied — +${r.data.friend_bonus} points!`); load(); } })
      .catch(() => {})
      .finally(() => { window.history.replaceState({}, '', '/collective'); });
  }, [load]);

  const redeem = async (rw) => {
    if (busy || !rw.affordable || !rw.tier_ok) return;
    setBusy(rw.id);
    try {
      const res = await axios.post(`${API}/collective/rewards/${rw.id}/redeem`, {}, { withCredentials: true });
      setToast(res.data?.message || 'Redeemed!');
      await load();
    } catch (e) { setToast(e?.response?.data?.detail || "Couldn't redeem"); }
    finally { setBusy(null); }
  };

  const rsvp = async (ev) => {
    if (busy || !ev.unlocked) return;
    setBusy(ev.id);
    try {
      const res = await axios.post(`${API}/collective/events/${ev.id}/rsvp`, {}, { withCredentials: true });
      setToast(res.data?.going ? "You're on the list 🎉" : 'RSVP removed');
      const r = await axios.get(`${API}/collective/events`, { withCredentials: true }).then((x) => x.data).catch(() => null);
      if (r) setEvents(r.events || []);
    } catch (e) { setToast(e?.response?.data?.detail || "Couldn't RSVP"); }
    finally { setBusy(null); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); setToast(label || `Copied ${text}`); } catch { /* clipboard blocked */ }
  };

  const shareReferral = () => {
    if (!referral) return;
    const link = `${window.location.origin}${referral.share_path}`;
    const text = `Join me on Homegrown 🌱 Use my code ${referral.code} and we both earn Collective points! ${link}`;
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else copy(link, 'Referral link copied');
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[70vh]"><div className="animate-spin w-8 h-8 border-2 border-[#1A4331] border-t-transparent rounded-full" /></div></Layout>;
  }

  const pv = card?.point_value || 100;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6 pb-24" data-testid="collective-page">
        <h1 className="text-2xl font-bold mb-1" style={{ color: C.text, fontFamily: 'Space Grotesk, sans-serif' }}>Homegrown Collective</h1>
        <p className="text-sm mb-5" style={{ color: C.muted }}>Your card, points and member rewards.</p>

        {/* Membership card */}
        <div className="rounded-3xl p-6 relative overflow-hidden shadow-xl border-2"
          style={{ background: `linear-gradient(135deg, ${C.forestDeep}, ${C.forest})`, borderColor: card?.tier?.color || C.gold }}
          data-testid="membership-card">
          <Sprout className="absolute -right-6 -bottom-8 opacity-[0.06]" size={190} color="#fff" />
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] tracking-[2px]" style={{ color: 'rgba(250,246,236,0.7)' }}>HOMEGROWN COLLECTIVE</div>
              <div className="text-xl font-bold mt-1" style={{ color: C.cream }}>{card?.display_name}</div>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full" style={{ background: card?.tier?.color || C.gold }} data-testid="card-tier-badge">
              <ShieldCheck size={13} color={C.cream} /><span className="text-xs font-semibold" style={{ color: C.cream }}>{card?.tier?.name}</span>
            </div>
          </div>
          <div className="flex items-end justify-between mt-6">
            <div>
              <div className="text-[11px]" style={{ color: 'rgba(250,246,236,0.7)' }}>POINTS BALANCE</div>
              <div className="text-4xl font-bold my-1" style={{ color: C.cream, fontFamily: 'Space Grotesk, sans-serif' }} data-testid="card-points">{(card?.points ?? 0).toLocaleString()}</div>
              <div className="text-xs" style={{ color: C.leaf }}>≈ ${((card?.points ?? 0) / pv).toFixed(2)} in rewards</div>
            </div>
            <button onClick={() => setQrBig(true)} className="bg-white rounded-xl p-2 flex flex-col items-center" data-testid="card-qr">
              <QRCodeCanvas value={card?.qr_value || 'HG'} size={104} fgColor="#0F2D1F" bgColor="#ffffff" />
              <span className="text-[10px] mt-1 font-semibold" style={{ color: C.forestDeep }}>TAP TO SCAN</span>
            </button>
          </div>
          <div className="flex items-center justify-between mt-6">
            <span className="text-sm tracking-widest" style={{ color: 'rgba(250,246,236,0.85)' }} data-testid="card-member-no">{card?.member_no}</span>
            <span className="text-[10px]" style={{ color: 'rgba(250,246,236,0.55)' }}>MEMBER</span>
          </div>
        </div>

        {/* Add to wallet (stubs) */}
        <div className="flex gap-2 mt-3">
          {[['Apple Wallet', Apple], ['Google Wallet', Award]].map(([w, Icon]) => (
            <button key={w} onClick={() => setToast(`${w} pass is coming soon — we'll connect it after launch.`)}
              data-testid={`add-${w.split(' ')[0].toLowerCase()}-wallet`}
              className="flex-1 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white" style={{ background: '#111' }}>
              <Icon size={16} color="#fff" /> Add to {w}
            </button>
          ))}
        </div>

        {/* Refer a friend */}
        {referral ? (
          <button onClick={shareReferral} data-testid="refer-friend"
            className="w-full flex items-center gap-3 mt-3 rounded-2xl p-4 border text-left"
            style={{ background: 'rgba(201,168,91,0.12)', borderColor: C.gold }}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: C.gold }}><Gift size={20} color={C.cream} /></div>
            <div className="flex-1">
              <div className="font-semibold" style={{ color: C.forest }}>Refer a friend</div>
              <div className="text-xs" style={{ color: C.muted }}>They get {referral.friend_bonus} pts to start — you get {referral.referrer_bonus} pts when they subscribe.{referral.completed ? ` · ${referral.completed} joined` : ''}</div>
            </div>
            <Share2 size={18} color={C.forest} />
          </button>
        ) : null}

        {/* Growth meter — plant grows toward the next tier */}
        {card ? (() => {
          const gp = card.next_tier ? Math.min(1, (card.points_12mo || 0) / card.next_tier.min) : 1;
          const idx = GROWTH_STAGES.reduce((acc, s, i) => (gp >= s.min ? i : acc), 0);
          const stage = card.next_tier ? GROWTH_STAGES[idx] : { Icon: Carrot, label: 'Fully grown 🌿' };
          const tint = card.tier?.color || C.leaf;
          const SIcon = stage.Icon;
          return (
            <div className="mt-3 rounded-2xl border p-4" style={{ background: C.cream, borderColor: C.border }} data-testid="growth-meter">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center transition-transform" style={{ borderColor: tint, background: 'rgba(107,159,74,0.12)' }}>
                  <SIcon size={28} color={tint} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold" style={{ color: C.forest }}>{stage.label}</div>
                  <div className="text-xs" style={{ color: C.muted }}>{card.next_tier ? `${card.next_tier.remaining.toLocaleString()} pts to ${card.next_tier.name}` : 'Top tier — best rewards unlocked'}</div>
                </div>
                <div className="text-2xl font-bold" style={{ color: tint, fontFamily: 'Space Grotesk, sans-serif' }}>{Math.round(gp * 100)}%</div>
              </div>
              <div className="h-3.5 rounded-full overflow-hidden mt-3" style={{ background: C.sage }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${gp * 100}%`, background: `linear-gradient(90deg, ${C.leaf}, ${C.gold})` }} />
              </div>
              <div className="flex justify-between mt-1.5 px-0.5">
                {GROWTH_STAGES.map((s, i) => { const MI = s.Icon; return <MI key={i} size={15} color={gp >= s.min ? tint : '#C7D0BE'} />; })}
              </div>
              <div className="text-[11px] mt-2" style={{ color: C.muted }}>Earn on Shop &amp; partner spend — your plant grows as your 12-month points climb.</div>
            </div>
          );
        })() : null}

        {/* Birthday prompt */}
        {card && !card.birthday ? (
          <button onClick={() => setBdOpen(true)} data-testid="birthday-prompt" className="w-full flex items-center gap-3 mt-3 rounded-2xl p-4 border text-left" style={{ background: 'rgba(226,114,91,0.1)', borderColor: C.terracotta }}>
            <Cake size={22} color={C.terracotta} />
            <div className="flex-1"><div className="font-semibold" style={{ color: C.forest }}>Add your birthday</div><div className="text-xs" style={{ color: C.muted }}>Get a surprise points gift on your special day.</div></div>
          </button>
        ) : null}

        {/* Tabs */}
        <div className="flex gap-2 mt-5 overflow-x-auto pb-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`collective-tab-${t.key}`}
              className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold border"
              style={tab === t.key ? { background: C.forest, color: C.cream, borderColor: C.forest } : { background: C.cream, color: C.forest, borderColor: C.border }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Rewards */}
        {tab === 'rewards' ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm" style={{ color: C.muted }}>Redeem your points. 100 points ≈ $1 of reward value.</p>
            {(rewards?.rewards || []).map((rw) => {
              const Icon = REWARD_ICON[rw.kind] || Gift;
              const can = rw.affordable && rw.tier_ok;
              return (
                <div key={rw.id} data-testid={`reward-${rw.id}`} className="flex items-center gap-3 rounded-2xl border p-4" style={{ background: C.cream, borderColor: C.border }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(26,67,49,0.08)' }}><Icon size={20} color={C.forest} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold" style={{ color: C.forest }}>{rw.title}</div>
                    <div className="text-xs" style={{ color: C.muted }}>{rw.description}</div>
                    <div className="text-xs font-semibold mt-1" style={{ color: C.gold }}>{rw.cost_points.toLocaleString()} pts</div>
                  </div>
                  <button onClick={() => redeem(rw)} disabled={!can || !!busy} data-testid={`redeem-${rw.id}`}
                    className="min-w-[92px] py-2 px-3 rounded-full text-sm font-semibold flex items-center justify-center gap-1"
                    style={can ? { background: C.forest, color: C.cream } : { background: C.sage, color: C.muted }}>
                    {busy === rw.id ? '…' : !rw.tier_ok ? <><Lock size={12} color={C.gold} /><span style={{ color: C.gold }}>{rw.min_tier_name}</span></>
                      : !rw.affordable ? `Need ${(rw.cost_points - (rewards?.points || 0)).toLocaleString()}` : 'Redeem'}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Partners */}
        {tab === 'partners' ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm" style={{ color: C.muted }}>Exclusive member offers on home &amp; veggie-garden gear.</p>
            {partners.map((p) => (
              <div key={p.id} data-testid={`partner-${p.id}`} className="rounded-2xl border p-4" style={{ background: C.cream, borderColor: C.border, opacity: p.unlocked ? 1 : 0.72 }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold" style={{ background: 'rgba(107,159,74,0.15)', color: C.forest }}>{(p.name?.[0] || '?').toUpperCase()}</div>
                  <div className="flex-1"><div className="font-semibold" style={{ color: C.forest }}>{p.name}</div><div className="text-[11px]" style={{ color: C.muted }}>{p.category}</div></div>
                  {!p.unlocked ? <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(201,168,91,0.15)', color: C.gold }}><Lock size={11} /> {p.min_tier_name}+</span> : null}
                </div>
                <div className="text-sm mt-2" style={{ color: C.text }}>{p.blurb}</div>
                <div className="flex items-center gap-1.5 mt-2 rounded-lg px-2 py-2" style={{ background: 'rgba(226,114,91,0.12)' }}>
                  <span className="text-sm font-semibold flex-1" style={{ color: C.forest }}>{p.offer}</span>
                </div>
                {p.unlocked && p.code ? (
                  <button onClick={() => copy(p.code)} data-testid={`partner-code-${p.id}`} className="w-full mt-2 flex items-center justify-center gap-2 border border-dashed rounded-lg py-2.5 font-semibold text-sm" style={{ borderColor: C.gold, color: C.forest }}>
                    {p.code} <Copy size={13} />
                  </button>
                ) : !p.unlocked ? <div className="text-[11px] mt-2" style={{ color: C.muted }}>Reach {p.min_tier_name} to unlock this offer.</div> : null}
              </div>
            ))}
          </div>
        ) : null}

        {/* Events */}
        {tab === 'events' ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm" style={{ color: C.muted }}>Members-only gatherings — RSVP to save your spot.</p>
            {events.map((ev) => (
              <div key={ev.id} data-testid={`event-${ev.id}`} className="rounded-2xl border p-4" style={{ background: C.cream, borderColor: C.border, opacity: ev.unlocked ? 1 : 0.72 }}>
                <div className="flex items-start gap-3">
                  <div className="w-14 rounded-lg py-1.5 flex flex-col items-center flex-shrink-0" style={{ background: 'rgba(226,114,91,0.12)' }}>
                    <span className="text-[11px] font-semibold" style={{ color: C.terracotta }}>{new Date(ev.starts_at).toLocaleDateString(undefined, { month: 'short' }).toUpperCase()}</span>
                    <span className="text-xl font-bold" style={{ color: C.forest }}>{new Date(ev.starts_at).getDate()}</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold" style={{ color: C.forest }}>{ev.title}</div>
                    <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: C.muted }}><MapPin size={11} /> {ev.location}</div>
                    {ev.spots_left != null ? <div className="text-[11px] mt-0.5" style={{ color: ev.spots_left <= 5 ? C.terracotta : C.muted }}>{ev.spots_left} spots left</div> : null}
                  </div>
                  {!ev.unlocked ? <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(201,168,91,0.15)', color: C.gold }}><Lock size={11} /> {ev.min_tier_name}+</span> : null}
                </div>
                <div className="text-sm mt-2" style={{ color: C.text }}>{ev.description}</div>
                <button onClick={() => rsvp(ev)} disabled={!ev.unlocked || !!busy} data-testid={`rsvp-${ev.id}`}
                  className="w-full mt-3 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5"
                  style={!ev.unlocked ? { background: C.sage, color: C.gold } : ev.going ? { background: C.leafDeep, color: C.cream } : { background: C.forest, color: C.cream }}>
                  {busy === ev.id ? '…' : !ev.unlocked ? <><Lock size={13} /> Reach {ev.min_tier_name}</> : ev.going ? <><Check size={14} /> You're going</> : 'RSVP'}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* Activity */}
        {tab === 'activity' ? (
          <div className="mt-4 space-y-2">
            {activity.length === 0 ? (
              <div className="text-center rounded-2xl border p-8" style={{ background: C.cream, borderColor: C.border }}><Clock size={22} color={C.muted} className="mx-auto" /><div className="text-sm mt-2" style={{ color: C.muted }}>No points activity yet.</div></div>
            ) : activity.map((a) => (
              <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-3 rounded-xl border p-3.5" style={{ background: C.cream, borderColor: C.border }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: a.points < 0 ? 'rgba(226,114,91,0.12)' : 'rgba(107,159,74,0.15)' }}>
                  {a.points < 0 ? <ArrowUpRight size={14} color={C.terracotta} /> : <ArrowDownLeft size={14} color={C.leafDeep} />}
                </div>
                <div className="flex-1"><div className="text-sm" style={{ color: C.text }}>{a.reason}</div><div className="text-[11px]" style={{ color: C.muted }}>{timeAgo(a.created_at)}</div></div>
                <span className="font-semibold" style={{ color: a.points < 0 ? C.terracotta : C.leafDeep }}>{a.points > 0 ? '+' : ''}{a.points.toLocaleString()}</span>
              </div>
            ))}
          </div>
        ) : null}

        {/* Tiers */}
        {tab === 'tiers' ? (
          <div className="mt-4 space-y-3">
            {(tiers?.tiers || []).map((t) => {
              const active = tiers?.current_key === t.key;
              return (
                <div key={t.key} data-testid={`tier-${t.key}`} className="rounded-3xl overflow-hidden shadow-sm" style={{ background: C.cream, border: active ? `2.5px solid ${t.accent || t.color}` : `1px solid ${C.border}` }}>
                  <div className="relative h-28 p-4 flex flex-col justify-between" style={{ backgroundImage: t.image ? `url(${t.image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${t.color}22, ${t.color}EE)` }} />
                    <div className="relative flex items-center justify-between">
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: t.color, color: C.cream }}><ShieldCheck size={14} /> {t.name}</span>
                      {active ? <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: t.accent || C.gold, color: C.forestDeep }}><Check size={12} /> YOUR TIER</span>
                        : <span className="px-2.5 py-1 rounded-full text-[11px]" style={{ background: 'rgba(0,0,0,0.35)', color: C.cream }}>{t.min.toLocaleString()}+ pts</span>}
                    </div>
                    <div className="relative font-semibold text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>{t.tagline}</div>
                  </div>
                  <div className="p-4 space-y-1.5">
                    {t.perks.map((perk, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" style={{ color: C.text }}>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${t.color}22` }}><Check size={11} color={t.color} /></span> {perk}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Full-screen scan mode — dimmed surround, QR + barcode */}
      {qrBig ? (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center p-6" style={{ background: '#05100A' }} onClick={() => setQrBig(false)} data-testid="qr-modal">
          <button onClick={() => setQrBig(false)} className="absolute top-8 right-6 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.14)' }} data-testid="scan-close"><X size={22} color="#fff" /></button>
          <div className="text-[11px] tracking-[2px] mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>HOMEGROWN COLLECTIVE</div>
          <div className="text-2xl font-bold mb-6 text-white">Scan at checkout</div>
          <div className="bg-white rounded-3xl p-6 flex flex-col items-center" style={{ width: 'min(88%, 360px)' }} onClick={(e) => e.stopPropagation()}>
            <QRCodeCanvas value={card?.qr_value || 'HG'} size={200} fgColor="#0F2D1F" bgColor="#ffffff" />
            <div className="h-px w-full my-4" style={{ background: C.border }} />
            <Barcode value={(card?.member_no || 'HG0000').replace(/-/g, '')} format="CODE128" height={62} width={2} displayValue={false} lineColor="#0F2D1F" background="#ffffff" />
            <div className="mt-3 font-semibold tracking-widest" style={{ color: C.forest }}>{card?.member_no}</div>
          </div>
          <div className="text-sm mt-6" style={{ color: 'rgba(255,255,255,0.7)' }}>Hold up to the partner scanner</div>
        </div>
      ) : null}

      {/* Birthday modal */}
      {bdOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setBdOpen(false)}>
          <div className="w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-6" style={{ background: C.cream }} onClick={(e) => e.stopPropagation()} data-testid="birthday-sheet">
            <div className="text-lg font-bold" style={{ color: C.text }}>🎂 Add your birthday</div>
            <div className="text-sm mb-3" style={{ color: C.muted }}>We'll drop a surprise points gift on your special day.</div>
            <div className="text-xs font-semibold mb-1" style={{ color: C.forest }}>Month</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => setBdMon(i + 1)} data-testid={`bd-month-${i + 1}`} className="flex-shrink-0 px-3 py-2 rounded-full text-sm font-semibold border" style={bdMon === i + 1 ? { background: C.forest, color: C.cream, borderColor: C.forest } : { background: '#fff', color: C.forest, borderColor: C.border }}>{m}</button>
              ))}
            </div>
            <div className="text-xs font-semibold mt-3 mb-1" style={{ color: C.forest }}>Day</div>
            <input value={bdDay} onChange={(e) => setBdDay(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))} inputMode="numeric" placeholder="e.g. 14" data-testid="bd-day" className="w-24 rounded-lg border px-4 py-3 text-base" style={{ borderColor: C.border, color: C.text }} />
            <button onClick={saveBirthday} disabled={bdMon === null || !bdDay} data-testid="bd-save" className="w-full mt-4 py-3 rounded-full font-semibold text-white disabled:opacity-50" style={{ background: C.forest }}>Save birthday</button>
          </div>
        </div>
      ) : null}

      {/* Birthday celebration */}
      {bdGift ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6" style={{ background: 'rgba(18,53,36,0.72)' }} onClick={() => setBdGift(null)} data-testid="birthday-celebration">
          <div className="rounded-3xl p-8 text-center max-w-sm w-full border-2 shadow-2xl" style={{ background: C.cream, borderColor: C.gold }} onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl">🎂</div>
            <div className="text-2xl font-bold mt-2" style={{ color: C.forest, fontFamily: 'Space Grotesk, sans-serif' }}>Happy Birthday!</div>
            <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full" style={{ background: 'rgba(201,168,91,0.18)', color: C.forestDeep }}><Gift size={16} /> <span className="font-semibold">+{bdGift.points} points added</span></div>
            <p className="mt-4 leading-relaxed" style={{ color: C.text }}>{bdGift.message}</p>
            <button onClick={() => setBdGift(null)} data-testid="birthday-celebration-close" className="w-full mt-5 py-3 rounded-full font-semibold text-white" style={{ background: C.forest }}>Thank you! 🌱</button>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-xl px-4 py-3 shadow-lg" style={{ background: C.forestDeep, color: C.cream }} data-testid="collective-toast">
          <CheckCircle2 size={15} /> <span className="text-sm">{toast}</span>
        </div>
      ) : null}
    </Layout>
  );
}

export default Collective;
